import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useFetch, usePromise, useLocalStorage } from '@rad/hooks';
import { useNotify } from '@rad/contexts/notifications';
import { staticForm, adjustForm, formValues, unsafeMapForm, formWithErrors } from '@rad/components/dynamic-form/common';
import { MANUALLY_ENTERED_CATEGORY, getScorecard, createScorecardMeasure, updateScorecardMeasure, getMeasures, getLookups } from '../types';
import LayoutForm from '../layouts/form';
const editableForm = (form, isAdmin) => ({ ...form, fields: form.fields.filter((f) => isAdmin || !f((x) => x.id === 'flags')) });
const constructForm = () => staticForm([
    [
        {
            'id': 'measure',
            'title': 'Measure',
            'kind': 'select',
            'value': '',
            'settings': {
                'source': {
                    'id': 'measure',
                },
                'autocomplete': true,
            },
            'required': true,
        },
        {
            'id': 'title',
            'title': 'Title',
            'kind': 'text',
            'value': '',
            'settings': {
                'special': 'plain',
            },
            'required': false,
        },
        {
            'id': 'direction',
            'title': 'Direction',
            'kind': 'select',
            'value': null,
            'settings': {
                'source': { 'id': 'measure-direction' },
                'tooltip': 'Direction tooltip...',
            },
            'required': false,
        },
        {
            'id': 'context',
            'title': 'Context',
            'kind': 'select',
            'value': '',
            'settings': { 'source': { 'id': null, 'options': Promise.resolve([]) }, 'tooltip': 'Leave blank to inherit the Scorecard context, then the Program default.' },
            'required': false,
        },
        {
            'id': 'filter',
            'title': 'Filter',
            'kind': 'text',
            'value': '',
            'settings': { 'special': 'multiline', 'tooltip': 'Combined with the Timeframe, Scorecard, and Measure filters using AND.' },
            'required': false,
        },
        {
            'id': 'flags',
            'title': 'Flags',
            'kind': 'text',
            'value': '',
            'settings': { 'special': 'plain' },
            'required': false,
        },
        {
            'id': 'precision',
            'title': 'Precision',
            'kind': 'select',
            'value': null,
            'settings': {
                'source': {
                    'id': 'measure-precision-sm'
                },
                'tooltip': 'Precision tooltip...',
            },
            'required': true,
        },
    ],
    [
        {
            'id': 'score_type',
            'title': 'Score Type',
            'kind': 'select',
            'value': null,
            'settings': {
                'source': {
                    'id': 'measure-score-type',
                },
                'tooltip': 'Score Type tooltip...',
            },
            'required': true,
        },
        {
            'id': 'units',
            'title': 'Units/Suffix',
            'kind': 'text',
            'value': '',
            'settings': { 'special': 'plain' },
            'required': false,
        },
        {
            'id': 'weight',
            'title': 'Weight',
            'kind': 'number',
            'value': null,
            'settings': {
                'tooltip': 'Weight tooltip...',
            },
            'required': true,
        },
        {
            'id': 'threshold',
            'title': 'Threshold',
            'kind': 'number',
            'value': null,
            'settings': {
                'tooltip': 'Threshold tooltip...',
            },
            'required': true,
        },
        {
            'id': 'target',
            'title': 'Target',
            'kind': 'number',
            'value': null,
            'settings': {
                'tooltip': 'Target tooltip...',
            },
            'required': true,
        },
        {
            'id': 'max',
            'title': 'Max',
            'kind': 'number',
            'value': null,
            'settings': {
                'tooltip': 'Max tooltip...',
            },
            'required': true,
        }
    ],
]);
const logic = (m, sm, lookups) => (x, values) => {
    /*
    Meeting                        -> Weight,Value per Point
    Sliding Scale                  -> Weight,Minimum Value,Baseline for Credit,Full Credit
    Reduce Score | Circuit Breaker -> N/A
    Other                          ->
        - Pass/Fail | Threshold/Target/Max -> Weight
        - Other                            -> Weight/Threshold,Target,Max
    */
    switch (x.id) {
        case 'context': return { ...x, settings: { ...x.settings, source: { id: null, options: Promise.resolve([
                        { id: '', label: 'Inherit Scorecard / Program context' },
                        ...lookups.filter((y) => y.kind === 'scorecard-context').map((y) => ({ id: y.value, label: y.title })),
                        ...(x.value && !lookups.some((y) => y.kind === 'scorecard-context' && y.value === x.value) ? [{ id: String(x.value), label: String(x.value) }] : []),
                    ]) } } };
        case 'measure': return { ...x, 'disabled': !!sm, 'settings': { ...x.settings, 'tooltip': m?.description } };
        case 'units': {
            const visible = m?.category === MANUALLY_ENTERED_CATEGORY && values['score_type'] === 'Number';
            return { ...x, hidden: !visible, value: visible ? x.value : '' };
        }
        case 'title': return { ...x, hidden: m?.category !== MANUALLY_ENTERED_CATEGORY, required: m?.category === MANUALLY_ENTERED_CATEGORY, value: m?.category === MANUALLY_ENTERED_CATEGORY ? x.value : '' };
        case 'direction': return { ...x, 'hidden': m?.category !== MANUALLY_ENTERED_CATEGORY };
        case 'score_type': return {
            ...x,
            'hidden': (m?.category !== MANUALLY_ENTERED_CATEGORY
                || ['SLIDING_SCALE', 'REDUCE_SCORE', 'CIRCUIT_BREAKER', 'MEETING'].includes(m.aggregation)),
        };
    }
    switch (x.id) {
        case 'weight': return { ...x, 'hidden': !m || ['REDUCE_SCORE', 'CIRCUIT_BREAKER'].includes(m.aggregation) };
        case 'threshold': return {
            ...x,
            'title': m?.aggregation === 'SLIDING_SCALE' ? 'Minimum Value' : m?.aggregation === 'MEETING' ? 'Value per Point' : 'Threshold',
            'hidden': !m || (['REDUCE_SCORE', 'CIRCUIT_BREAKER'].includes(m.aggregation)
                || ['Pass/Fail', 'Threshold/Target/Max'].includes(m.score_type)
                || (m?.category === MANUALLY_ENTERED_CATEGORY && ['Pass/Fail', 'Threshold/Target/Max'].includes(values['score_type']))),
        };
        case 'target': return {
            ...x,
            'title': m?.aggregation === 'SLIDING_SCALE' ? 'Baseline for Credit' : 'Target',
            'hidden': !m || (['MEETING', 'REDUCE_SCORE', 'CIRCUIT_BREAKER'].includes(m.aggregation)
                || ['Pass/Fail', 'Threshold/Target/Max'].includes(m.score_type)
                || (m?.category === MANUALLY_ENTERED_CATEGORY && ['Pass/Fail', 'Threshold/Target/Max'].includes(values['score_type']))),
        };
        case 'max': return {
            ...x,
            'title': m?.aggregation === 'SLIDING_SCALE' ? 'Full Credit' : 'Max',
            'hidden': !m || (['MEETING', 'REDUCE_SCORE', 'CIRCUIT_BREAKER'].includes(m.aggregation)
                || ['Pass/Fail', 'Threshold/Target/Max'].includes(m.score_type)
                || (m?.category === MANUALLY_ENTERED_CATEGORY && ['Pass/Fail', 'Threshold/Target/Max'].includes(values['score_type']))),
        };
    }
    return { ...x, 'disabled': !values['measure'] };
};
const ScorecardMeasureNew = (props) => {
    const [token] = useLocalStorage('token');
    const notify = useNotify();
    const [lookupsPromise] = useFetch(getLookups());
    const lookups = usePromise(lookupsPromise) || [];
    const [measuresPromise] = useFetch(getMeasures());
    const measures = usePromise(measuresPromise);
    const [stateForm, setForm] = React.useState(constructForm());
    const selectedMeasureId = ((f) => f ? f((x) => x.value) : '')(stateForm.fields.find((f) => f((x) => x.id === 'measure')));
    const adjustedForm = adjustForm(logic(measures?.find((x) => x.id === selectedMeasureId), undefined, lookups))(stateForm);
    const [stateErrors, setErrors] = React.useState(undefined);
    const submit = async (e) => {
        e.preventDefault();
        const [valid, result] = await createScorecardMeasure({
            'scorecard_id': props.scorecard_id,
            ...formValues(editableForm(adjustedForm, token?.is_admin === true)),
        });
        if (valid) {
            notify({ 'kind': 'success', 'message': 'Measure added successfully!' });
            window.location.hash = `#/scorecards/${props.scorecard_id}`;
        }
        else {
            setErrors(result);
        }
    };
    { /* {measures && values['measure'] && measures.find((x) => x.id === values['measure'])?.description} */ }
    return _jsx(LayoutForm, { toolbarRight: _jsx(_Fragment, { children: _jsx("a", { href: "#/measures/request", className: "btn btn-sm btn-primary", children: "Request New Measure" }) }), title: "Add Measure to Scorecard", form: formWithErrors(editableForm(adjustedForm, token?.is_admin === true), stateErrors), onChange: setForm, onSubmit: submit });
};
const ScorecardMeasureEdit = (props) => {
    const [token] = useLocalStorage('token');
    const notify = useNotify();
    const [lookupsPromise] = useFetch(getLookups());
    const lookups = usePromise(lookupsPromise) || [];
    const [scorecardPromise] = useFetch(getScorecard(props.scorecard_id));
    const scorecard = usePromise(scorecardPromise);
    const scorecardMeasure = scorecard?.measures.find((x) => x.id === props.id);
    const [stateForm, setForm] = React.useState(constructForm());
    const adjustedForm = adjustForm(logic(scorecardMeasure?.measure, scorecardMeasure, lookups))(stateForm);
    const [stateErrors, setErrors] = React.useState(undefined);
    const submit = async (e) => {
        e.preventDefault();
        const [valid, result] = await updateScorecardMeasure({
            'id': props.id,
            'scorecard_id': props.scorecard_id,
            ...formValues(editableForm(adjustedForm, token?.is_admin === true)),
        });
        if (valid) {
            notify({ 'kind': 'success', 'message': 'Measure updated successfully!' });
            window.location.hash = `#/scorecards/${props.scorecard_id}`;
        }
        else {
            setErrors(result);
        }
    };
    React.useEffect(() => {
        setForm(unsafeMapForm(stateForm, (x) => {
            if (!!scorecardMeasure) {
                switch (x.id) {
                    case 'measure':
                        return { ...x, 'value': scorecardMeasure.measure_id };
                    case 'title':
                        return { ...x, 'value': scorecardMeasure.title };
                    case 'context':
                        return { ...x, 'value': scorecardMeasure.context || '' };
                    case 'filter':
                        return { ...x, 'value': scorecardMeasure.filter || '' };
                    case 'flags':
                        return { ...x, 'value': scorecardMeasure.flags || '' };
                    case 'score_type':
                        return { ...x, 'value': scorecardMeasure.score_type };
                    case 'units':
                        return { ...x, 'value': scorecardMeasure.units || '' };
                    case 'direction':
                        return { ...x, 'value': scorecardMeasure.direction };
                    case 'precision':
                        return { ...x, 'value': scorecardMeasure.precision === null ? null : String(scorecardMeasure.precision) };
                    case 'weight':
                        return { ...x, 'value': scorecardMeasure.weight };
                    case 'threshold':
                        return { ...x, 'value': scorecardMeasure.threshold };
                    case 'target':
                        return { ...x, 'value': scorecardMeasure.target };
                    case 'max':
                        return { ...x, 'value': scorecardMeasure.max };
                    default:
                        return x;
                }
            }
            else {
                return x;
            }
        }));
    }, [scorecardMeasure]);
    if (scorecard === undefined) {
        return _jsx(_Fragment, { children: "Loading..." });
    }
    else if (scorecard === null || scorecardMeasure === undefined) {
        return _jsx(_Fragment, { children: "Not Found" });
    }
    else {
        return _jsx(LayoutForm, { toolbarRight: _jsx(_Fragment, {}), title: _jsxs(_Fragment, { children: ["Edit Measure ", _jsx("em", { children: scorecardMeasure.display }), _jsx("br", {}), _jsxs("span", { className: "d-block small text-muted mt-1", children: ["on Scorecard ", _jsx("em", { children: scorecard.display })] })] }), form: formWithErrors(editableForm(adjustedForm, token?.is_admin === true), stateErrors), onChange: setForm, onSubmit: submit });
    }
};
const ScorecardMeasureView = (prop) => {
    return _jsx(_Fragment, { children: "Not Implemented" });
};
const ScorecardMeasureScreen = (props) => props.id !== undefined && props.edit
    ? _jsx(ScorecardMeasureEdit, { scorecard_id: props.scorecard_id, id: props.id })
    : props.id !== undefined
        ? _jsx(ScorecardMeasureView, { scorecard_id: props.scorecard_id, id: props.id })
        : _jsx(ScorecardMeasureNew, { scorecard_id: props.scorecard_id });
export default ScorecardMeasureScreen;
